/**
 * Google reCAPTCHA v3 for Shopify theme forms.
 * Badge loads globally via recaptcha.liquid; this binds protected forms.
 * Tokens are verified in the browser only and are never included in form POST data.
 */
(function () {
  const configEl = document.getElementById('recaptcha-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  if (!config.enabled || !config.siteKey) return;

  const bindCollapsedBadge = (badge) => {
    if (!window.matchMedia('(hover: none)').matches) return;

    badge.addEventListener('click', (event) => {
      event.stopPropagation();
      badge.classList.toggle('recaptcha-badge--expanded');
    });

    document.addEventListener('click', () => {
      badge.classList.remove('recaptcha-badge--expanded');
    });
  };

  const waitForBadge = (attempts = 0) => {
    const badge = document.querySelector('.grecaptcha-badge');
    if (badge) {
      bindCollapsedBadge(badge);
      return;
    }
    if (attempts < 50) {
      window.setTimeout(() => waitForBadge(attempts + 1), 200);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForBadge(), { once: true });
  } else {
    waitForBadge();
  }

  const forms = document.querySelectorAll('form.js-recaptcha-form');
  if (!forms.length) return;

  const waitForRecaptcha = () =>
    new Promise((resolve, reject) => {
      if (window.grecaptcha?.execute) {
        resolve(window.grecaptcha);
        return;
      }

      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (window.grecaptcha?.execute) {
          window.clearInterval(timer);
          resolve(window.grecaptcha);
          return;
        }
        if (attempts > 100) {
          window.clearInterval(timer);
          reject(new Error('reCAPTCHA failed to load'));
        }
      }, 100);
    });

  const getTokenInput = (form) => form.querySelector('[data-recaptcha-token-input]');

  const getAction = (form) => {
    const input = getTokenInput(form);
    return input?.dataset.recaptchaAction || form.dataset.recaptchaAction || 'submit';
  };

  const ensureTokenInput = (form) => {
    let input = getTokenInput(form);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.setAttribute('data-recaptcha-token-input', '');
      input.setAttribute('tabindex', '-1');
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-hidden', 'true');
      form.appendChild(input);
    }
    input.removeAttribute('name');
    input.removeAttribute('id');
    return input;
  };

  const removeRecaptchaFromForm = (form) => {
    form.querySelectorAll('[data-recaptcha-token-input]').forEach((el) => el.remove());
    form.querySelectorAll('input[name], textarea[name], select[name]').forEach((el) => {
      const name = (el.getAttribute('name') || '').toLowerCase();
      if (/recaptcha|captcha|g-recaptcha/.test(name)) el.remove();
    });
  };

  const verifyToken = async (token, action) => {
    if (!config.verifyUrl) return true;

    const response = await fetch(config.verifyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ token, action }),
    });

    if (!response.ok) return false;

    const result = await response.json();
    return result.success === true && (result.score == null || result.score >= config.minScore);
  };

  const showError = (form) => {
    let error = form.querySelector('[data-recaptcha-error]');
    if (!error) {
      error = document.createElement('p');
      error.className = 'recaptcha-form-error';
      error.setAttribute('data-recaptcha-error', '');
      error.setAttribute('role', 'alert');
      error.textContent = config.errorMessage;
      form.prepend(error);
    }
    error.hidden = false;
  };

  const bindForm = (form) => {
    removeRecaptchaFromForm(form);

    form.addEventListener('submit', async (event) => {
      if (form.dataset.recaptchaSubmitting === 'true') return;

      event.preventDefault();
      const submitter = form.querySelector('[type="submit"]');
      if (submitter) submitter.disabled = true;

      const action = getAction(form);
      const tokenInput = ensureTokenInput(form);

      try {
        const grecaptcha = await waitForRecaptcha();
        await new Promise((resolve) => grecaptcha.ready(resolve));

        const token = await grecaptcha.execute(config.siteKey, { action });

        const verified = await verifyToken(token, action);
        if (!verified) {
          showError(form);
          if (submitter) submitter.disabled = false;
          return;
        }

        removeRecaptchaFromForm(form);

        form.dataset.recaptchaSubmitting = 'true';
        form.submit();
      } catch {
        showError(form);
        if (submitter) submitter.disabled = false;
      } finally {
        tokenInput.value = '';
      }
    });
  };

  forms.forEach(bindForm);
})();
